import { useEffect, useMemo } from "react";
import dayjs, { type Dayjs } from "dayjs";
import type { TFunction } from "i18next";
import { type DrinkActivitySummary, type DrinkOption } from "../../services/drinkService";
import { toAbvPercent } from "../../services/drinks/utils/numbers";
import { DRINKING_DAY_SHIFT_HOURS } from "./drinkStats/constants";
import { buildDailyPromilleTimeline, buildFullPromilleTimeline, calculateMaxPromilleInRange, computeVolumeTotals, deriveSoberInfo, getPromilleAtTime } from "./drinkStats/utils";
import type { Sex } from "./drinkStats/types";
import type { StatItem } from "./drinkStats/SummaryStatsGrid";
import { calculateAlcoholTotals, computeBreathalyzerEstimate, getLatestActivityTimestamp, getSelectedDayBounds, selectActivitiesForDay } from "./drinkStats/derived";

const MS_IN_DAY = 24 * 60 * 60 * 1000;
const DRINKING_DAY_SHIFT_MS = DRINKING_DAY_SHIFT_HOURS * 60 * 60 * 1000;

type Props = {
    activities: DrinkActivitySummary[];
    availableDrinks: DrinkOption[];
    selectedDay: Dayjs;
    setSelectedDay: React.Dispatch<React.SetStateAction<Dayjs>>;
    hasUserSelectedDay: boolean;
    weightKg: number;
    sex: Sex;
    nowMs: number;
    t: TFunction;
};

export const useDrinkStatsDerived = ({ activities, availableDrinks, selectedDay, setSelectedDay, hasUserSelectedDay, weightKg, sex, nowMs, t }: Props) => {
    const latestActivityTimestamp = useMemo(() => getLatestActivityTimestamp(activities), [activities]);
    useEffect(() => {
        if (hasUserSelectedDay) return;
        if (!latestActivityTimestamp) {
            setSelectedDay((current) => {
                const today = dayjs().startOf("day");
                return current.isSame(today) ? current : today;
            });
            return;
        }
        const latestDay = dayjs(latestActivityTimestamp).subtract(DRINKING_DAY_SHIFT_HOURS, "hour").startOf("day");
        setSelectedDay((current) => current.isSame(latestDay) ? current : latestDay);
    }, [latestActivityTimestamp, hasUserSelectedDay, setSelectedDay]);

    const selectedDayRangeMs = useMemo(() => {
        const start = selectedDay.startOf("day").valueOf() + DRINKING_DAY_SHIFT_MS;
        return { start, end: start + MS_IN_DAY };
    }, [selectedDay]);
    const selectedDayDisplay = useMemo(() => selectedDay.add(DRINKING_DAY_SHIFT_HOURS, "hour"), [selectedDay]);
    const selectedDayActivities = useMemo(() => selectActivitiesForDay(activities, selectedDayRangeMs.start, selectedDayRangeMs.end), [activities, selectedDayRangeMs]);
    const selectedDayBounds = useMemo(() => getSelectedDayBounds(selectedDayActivities, selectedDayRangeMs.start, selectedDayRangeMs.end), [selectedDayActivities, selectedDayRangeMs]);
    const fullPromilleTimeline = useMemo(() => buildFullPromilleTimeline(activities, weightKg, sex, nowMs), [activities, weightKg, sex, nowMs]);
    const selectedDayTimeline = useMemo(() => buildDailyPromilleTimeline(selectedDayActivities, selectedDayBounds.start, selectedDayBounds.end, weightKg, sex, nowMs), [selectedDayActivities, selectedDayBounds, weightKg, sex, nowMs]);
    const selectedDayPeakPromille = useMemo(() => calculateMaxPromilleInRange(selectedDayTimeline, selectedDayRangeMs.start, selectedDayRangeMs.end), [selectedDayTimeline, selectedDayRangeMs]);
    const selectedDaySoberInfo = useMemo(() => deriveSoberInfo(selectedDayTimeline, nowMs), [selectedDayTimeline, nowMs]);
    const volumeTotals = useMemo(() => computeVolumeTotals(activities, selectedDayRangeMs.start, selectedDayRangeMs.end), [activities, selectedDayRangeMs]);
    const alcoholTotals = useMemo(() => calculateAlcoholTotals(activities, selectedDayRangeMs.start, selectedDayRangeMs.end), [activities, selectedDayRangeMs]);
    const maxPromilleByRange = useMemo(() => {
        const end = Math.min(selectedDayBounds.end, nowMs);
        const start = selectedDayBounds.start;
        return {
            week: calculateMaxPromilleInRange(fullPromilleTimeline, start - 6 * MS_IN_DAY, end),
            month: calculateMaxPromilleInRange(fullPromilleTimeline, start - 29 * MS_IN_DAY, end),
        };
    }, [fullPromilleTimeline, selectedDayBounds, nowMs]);
    const volumeStats: StatItem[] = useMemo(() => [
        { key: "volume-day", title: t("drinkStats.summary.volumeDay", { date: selectedDayDisplay.format("DD MMM YYYY") }), value: volumeTotals.day, suffix: "ml" },
        { key: "volume-week", title: t("drinkStats.summary.volumeWeek", { date: selectedDayDisplay.format("DD MMM YYYY") }), value: volumeTotals.week, suffix: "ml" },
        { key: "volume-month", title: t("drinkStats.summary.volumeMonth", { date: selectedDayDisplay.format("DD MMM YYYY") }), value: volumeTotals.month, suffix: "ml" },
    ], [selectedDayDisplay, t, volumeTotals]);
    const alcoholStats: StatItem[] = useMemo(() => [
        { key: "alcohol-day", title: t("drinkStats.summary.alcoholDay"), value: alcoholTotals.day, suffix: "g", precision: 1 },
        { key: "alcohol-week", title: t("drinkStats.summary.alcoholWeek"), value: alcoholTotals.week, suffix: "g", precision: 1 },
        { key: "alcohol-month", title: t("drinkStats.summary.alcoholMonth"), value: alcoholTotals.month, suffix: "g", precision: 1 },
    ], [alcoholTotals, t]);
    const promilleStats: StatItem[] = useMemo(() => [
        { key: "max-week", title: t("drinkStats.summary.promilleWeek"), value: maxPromilleByRange.week, suffix: "‰", precision: 3 },
        { key: "max-month", title: t("drinkStats.summary.promilleMonth"), value: maxPromilleByRange.month, suffix: "‰", precision: 3 },
    ], [maxPromilleByRange, t]);
    const hasSelectedDayData = selectedDayActivities.length > 0 || selectedDayTimeline.some((point) => point.promille > 0);
    const selectedDayActivitiesDesc = useMemo(() => [...selectedDayActivities].sort((a, b) => (b.timestamp?.getTime() ?? 0) - (a.timestamp?.getTime() ?? 0)), [selectedDayActivities]);
    const drinkSelectOptions = useMemo(() => availableDrinks.map((drink) => ({
        value: drink.id,
        label: t("drinkStats.drinkOptionLabel", { drink: drink.name, abv: toAbvPercent(drink.promille).toFixed(1) }),
    })), [availableDrinks, t]);
    const isSelectedDayToday = useMemo(() => selectedDay.isSame(dayjs(), "day"), [selectedDay]);
    const latestPromilleRaw = useMemo(() => {
        if (!selectedDayTimeline.length) return 0;
        if (isSelectedDayToday) {
            const timelineEnd = selectedDayTimeline.at(-1)!.timestamp;
            return Number(getPromilleAtTime(selectedDayTimeline, Math.min(nowMs, timelineEnd)).toFixed(3));
        }
        return selectedDayPeakPromille;
    }, [selectedDayTimeline, isSelectedDayToday, nowMs, selectedDayPeakPromille]);
    const { policePromille: latestPromille, breathMgL: latestBreathMgL } = useMemo(() => computeBreathalyzerEstimate(latestPromilleRaw), [latestPromilleRaw]);

    return { selectedDayDisplay, selectedDayActivities, selectedDayTimeline, selectedDaySoberInfo,
        volumeStats, alcoholStats, promilleStats, hasSelectedDayData,
        selectedDayActivitiesDesc, drinkSelectOptions,
        isSelectedDayToday, latestPromilleRaw, latestPromille, latestBreathMgL, selectedDayPeakPromille };
};
